import { useEffect, useState } from "react";
import type { GroupInfo, MaterialKind } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState } from "../../../../state/editor/useEditor";
import { ALL_PARTS, type BasicScope } from "../applyEngine";

/** Which parts a preset panel offers: one kind, or every part ("any"). */
export type PartPool = MaterialKind | "any";

/** The pick for "every part" on a pool whose "All" means one kind (`allKind`). */
export const EVERY_PART = "every";

/**
 * "Apply to: All metal · Band · Bezels" — which parts a preset panel changes.
 *
 * It follows the viewer's selection: click the band in the 3D view and the
 * Metal panel is already scoped to the band. Picking a part chip selects it in
 * the viewer too, so the outline shows which part will change; "All" clears
 * the selection.
 *
 * `also` widens a kind's pool with parts of the other kind that belong in it —
 * the Pearls panel offers the stones plus any part that is already a pearl.
 *
 * `allKind` narrows what "All" means on an "any" pool: Other's "All" is every
 * METAL part, because an enamel or a wood put on "all parts" also re-files the
 * stones as metal. Every part is then one more chip away ("every").
 *
 * Returns `scope` for the look-field engine (kind pools only) and `ids`, the
 * parts an apply should reach right now.
 */
export function usePartScope(pool: PartPool, also?: (g: GroupInfo) => boolean, opts: { allKind?: MaterialKind } = {}) {
  const { client } = useViewer();
  const groups = (useDomainState("groups") as GroupInfo[] | undefined) ?? [];
  const selection = useDomainState("selection") as string | null;
  const parts = groups.filter((g) => pool === "any" || g.kind === pool || (also?.(g) ?? false));
  const selectedPart = parts.find((g) => g.id === selection)?.id ?? null;
  const [pick, setPick] = useState<string>(selectedPart ?? "all");

  useEffect(() => {
    if (selectedPart) setPick(selectedPart);
  }, [selectedPart]);

  // A part that disappears (new model, reclassified) falls back to "all".
  const whole = pick === "all" || (pick === EVERY_PART && !!opts.allKind);
  const valid = whole || parts.some((p) => p.id === pick) ? pick : "all";

  const choose = (id: string) => {
    setPick(id);
    if (id === "all" || id === EVERY_PART) void client.ring.clearSelection();
    else void client.ring.selectGroup(id);
  };

  const scope: BasicScope = pool === "any" ? ALL_PARTS : { ...ALL_PARTS, [pool]: valid === EVERY_PART ? "all" : valid };
  const ids =
    valid === "all"
      ? parts.filter((p) => !opts.allKind || p.kind === opts.allKind).map((p) => p.id)
      : valid === EVERY_PART
        ? parts.map((p) => p.id)
        : [valid];
  return { parts, pick: valid, choose, scope, ids };
}

/**
 * A part's display name, readable: CAD layer names are kept (they are the
 * jeweller's own words) but tidied — "METAL01" → "Metal 01", "18K_Rose" →
 * "18K Rose", "gem_01" → "Gem 01". The raw name stays in the tooltip.
 */
export function partName(g: GroupInfo, i: number, kind: MaterialKind): string {
  const raw = (g.label || "").trim();
  if (!raw) return `${kind === "metal" ? "Metal" : "Stone"} ${i + 1}`;
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/([A-Za-z])(\d)/g, "$1 $2")
    .split(/\s+/)
    // Shouting words ("METAL") calm down; short ones are trade abbreviations
    // ("PT", "CZ", "YG") and keep their capitals. Only the first word is
    // capitalised — "White gold band" stays as written.
    .map((w, j) => (/^[A-Z]{4,}$/.test(w) ? w[0] + w.slice(1).toLowerCase() : j === 0 && /^[a-z]/.test(w) ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}
